import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";

import { useCurrentUser } from "@/hooks/useCurrentUser";

interface RequireAuthProps {
  children: React.ReactNode;
  /** Optional role gate — if set, redirect to / when below this rank. */
  minRole?: "director" | "ceo" | "admin";
}

/**
 * Wrap any page (or layout) that needs a session. Calls /api/v1/me/
 * via React Query; while it's loading, shows a centered spinner —
 * never a flash of "you're not signed in" before the real answer
 * comes back. On 403/401 bounces to /login with `?next=...` so the
 * post-login redirect lands where the user actually wanted to go.
 *
 * Pass `minRole` for role-gated routes:
 *   <RequireAuth minRole="ceo"><CeoSettings /></RequireAuth>
 */
export function RequireAuth({ children, minRole }: RequireAuthProps) {
  const { data: user, isLoading } = useCurrentUser();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (minRole) {
    const passes =
      (minRole === "director" && user.is_director_level)
      || (minRole === "ceo"   && user.is_ceo_level)
      || (minRole === "admin" && user.is_admin_level);
    if (!passes) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
